/**
 * Contribution Summary Document
 * Simple summary document with no signatures required
 */

import { replaceMergeFields, getAllMergeFields } from './mergeFields';

export function generateContributionSummaryHtml(
  workflowData: any,
  unaData: any
): string {
  const mergeFields = getAllMergeFields(workflowData, unaData);
  
  const template = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    @page {
      size: Letter;
      margin: 0.75in;
    }
    body {
      font-family: Cambria, Georgia, 'Times New Roman', serif;
      font-size: 11pt;
      line-height: 1.85;
      color: #1a1a1a;
      max-width: 8.5in;
      margin: 0 auto;
      padding: 0;
    }
    h1 {
      text-align: center;
      font-size: 18pt;
      margin: 0 0 20pt 0;
      font-weight: bold;
      text-transform: uppercase;
      letter-spacing: 0.12em;
      border-bottom: 2px solid #c9a961;
      padding-bottom: 14pt;
    }
    h2 {
      font-size: 13pt;
      margin: 24pt 0 10pt 0;
      font-weight: bold;
      border-bottom: 1px solid #bfbfbf;
      padding-bottom: 6pt;
      color: #262626;
    }
    .section {
      margin-bottom: 16pt;
    }
    .label {
      font-weight: bold;
      display: inline-block;
      min-width: 2in;
    }
    .value {
      display: inline;
    }
    .purpose {
      margin-top: 14pt;
      padding: 14pt;
      background-color: #fafafa;
      border-left: 4px solid #c9a961;
      font-size: 10pt;
    }
    .footer {
      margin-top: 30pt;
      padding-top: 16pt;
      border-top: 1px solid #ccc;
      font-size: 9pt;
      color: #666;
      text-align: center;
    }
  </style>
</head>
<body>
  <h1>Contribution Summary</h1>
  
  <div class="section">
    <h2>UNA Information</h2>
    <p><span class="label">UNA Name:</span> <span class="value">{{una_name}}</span></p>
    <p><span class="label">State:</span> <span class="value">{{una_state}}</span></p>
  </div>
  
  <div class="section">
    <h2>Contributor Information</h2>
    <p><span class="label">Name:</span> <span class="value">{{contributor_name}}</span></p>
    <p><span class="label">Role:</span> <span class="value">{{contributor_role}}</span></p>
    <p><span class="label">Address:</span> <span class="value">{{contributor_address}}</span></p>
  </div>
  
  <div class="section">
    <h2>Contribution Details</h2>
    <p><span class="label">Asset Type:</span> <span class="value">{{asset_type}}</span></p>
    <p><span class="label">Contribution Date:</span> <span class="value">{{contribution_date}}</span></p>
    <p><span class="label">Contribution Method:</span> <span class="value">{{contribution_method}}</span></p>
    <p><span class="label">Receiving Entity:</span> <span class="value">{{receiving_entity}}</span></p>
  </div>
  
  ${getAssetSpecificSection(workflowData)}
  
  <div class="section">
    <h2>Purpose of Contribution</h2>
    <div class="purpose">
      {{contribution_purpose}}
    </div>
  </div>
  
  <div class="footer">
    This summary document was generated on {{current_date}}<br>
    for ecclesiastical record-keeping purposes within {{una_name}}
  </div>
</body>
</html>
  `;
  
  return replaceMergeFields(template, mergeFields);
}

function getAssetSpecificSection(workflowData: any): string {
  const assetType = workflowData.asset_type;
  
  if (assetType?.includes('Business Entity')) {
    return `
  <div class="section">
    <h2>Business Entity Details</h2>
    <p><span class="label">Entity Name:</span> <span class="value">{{entity_legal_name}}</span></p>
    <p><span class="label">Entity Type:</span> <span class="value">{{entity_type}}</span></p>
    <p><span class="label">State of Formation:</span> <span class="value">{{entity_state_of_formation}}</span></p>
    <p><span class="label">Formation Date:</span> <span class="value">{{entity_formation_date}}</span></p>
    <p><span class="label">EIN:</span> <span class="value">{{entity_ein}}</span></p>
    <p><span class="label">Ownership Percentage:</span> <span class="value">{{ownership_percentage}}%</span></p>
    <p><span class="label">Business Purpose:</span> <span class="value">{{entity_business_purpose}}</span></p>
  </div>
    `;
  } else if (assetType?.includes('Real Estate')) {
    return `
  <div class="section">
    <h2>Real Estate Details</h2>
    <p><span class="label">Property Address:</span> <span class="value">{{property_address}}, {{property_city}}, {{property_state}} {{property_zip}}</span></p>
    <p><span class="label">County:</span> <span class="value">{{property_county}}</span></p>
    <p><span class="label">Property Type:</span> <span class="value">{{property_type_detail}}</span></p>
    <p><span class="label">APN:</span> <span class="value">{{property_apn}}</span></p>
    <p><span class="label">Purpose:</span> <span class="value">{{property_purpose}}</span></p>
  </div>
    `;
  } else if (assetType?.includes('Vehicle')) {
    return `
  <div class="section">
    <h2>Vehicle Details</h2>
    <p><span class="label">Vehicle:</span> <span class="value">{{vehicle_description}}</span></p>
    <p><span class="label">VIN:</span> <span class="value">{{vehicle_vin}}</span></p>
    <p><span class="label">Color:</span> <span class="value">{{vehicle_color}}</span></p>
    <p><span class="label">Purpose:</span> <span class="value">{{vehicle_purpose}}</span></p>
  </div>
    `;
  } else if (assetType === 'Intellectual Property') {
    return `
  <div class="section">
    <h2>Intellectual Property Details</h2>
    <p><span class="label">IP Type:</span> <span class="value">{{ip_type}}</span></p>
    <p><span class="label">Description:</span> <span class="value">{{ip_description}}</span></p>
  </div>
    `;
  } else if (assetType === 'Digital/Cryptocurrency') {
    return `
  <div class="section">
    <h2>Digital Asset Details</h2>
    <p><span class="label">Asset Type:</span> <span class="value">{{digital_asset_type}}</span></p>
    <p><span class="label">Blockchain Network:</span> <span class="value">{{blockchain_network}}</span></p>
    <p><span class="label">Description:</span> <span class="value">{{asset_description}}</span></p>
  </div>
    `;
  } else if (assetType === 'Cash/Bank Account') {
    return `
  <div class="section">
    <h2>Cash Contribution Details</h2>
    <p><span class="label">Description:</span> <span class="value">{{contribution_description}}</span></p>
    <p><span class="label">Account Type:</span> <span class="value">{{account_type}}</span></p>
    <p><span class="label">Bank:</span> <span class="value">{{bank_name}}</span></p>
  </div>
    `;
  } else if (assetType === 'Personal Property') {
    return `
  <div class="section">
    <h2>Personal Property Details</h2>
    <p><span class="label">Category:</span> <span class="value">{{property_category}}</span></p>
    <p><span class="label">Condition:</span> <span class="value">{{property_condition}}</span></p>
    <p><span class="label">Location:</span> <span class="value">{{property_location}}</span></p>
    <p><span class="label">Description:</span> <span class="value">{{property_description}}</span></p>
  </div>
    `;
  } else {
    return `
  <div class="section">
    <h2>Asset Description</h2>
    <p>{{other_asset_description}}</p>
  </div>
    `;
  }
}
